import { useState } from 'react';
import { Check, CheckCircle2, Copy, PenLine, XCircle } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { linkBiglietto, quantiNelModulo, type BigliettoPubblico } from '../../../lib/eventi/bigliettiPubblici';

type Props = {
  biglietti: BigliettoPubblico[];
  onCompila: (biglietto: BigliettoPubblico) => void;
};

function Stato({ b }: { b: BigliettoPubblico }) {
  if (b.stato === 'registrato') {
    return (
      <span className="inline-flex items-center gap-1.5 text-base font-medium text-green-700">
        <CheckCircle2 className="h-5 w-5" />
        {b.partecipante ? `Registrato: ${b.partecipante}` : 'Registrato'}
      </span>
    );
  }
  if (b.stato === 'annullato') {
    return (
      <span className="inline-flex items-center gap-1.5 text-base font-medium text-red-700">
        <XCircle className="h-5 w-5" />
        Annullato
      </span>
    );
  }
  return <span className="text-base font-medium text-amber-700">Da registrare</span>;
}

/** Copia il link del biglietto e lo dice per due secondi. */
function CopiaLink({ token }: { token: string }) {
  const [copiato, setCopiato] = useState(false);
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(linkBiglietto(token));
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      // Senza permesso per gli appunti si mostra il link da copiare a mano.
      window.prompt('Copia il link e mandalo a chi userà il biglietto:', linkBiglietto(token));
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copia()}
      className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-800 ring-1 ring-gray-300 transition hover:bg-gray-50"
    >
      {copiato ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      {copiato ? 'Link copiato' : 'Copia il link per un collega'}
    </button>
  );
}

/**
 * I biglietti di chi ha comprato, uno per riga: per ognuno si compila il
 * modulo qui, oppure si copia il link da mandare a chi lo usera' (cosi'
 * ognuno mette i suoi dati e da' i suoi consensi). Congressista e Workshop
 * dello stesso ordine sono una riga sola: un modulo vale per tutti e due.
 */
export function ElencoBiglietti({ biglietti, onCompila }: Props) {
  return (
    <ol className="foglio-quadretti mt-6 divide-y divide-gray-200 overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5">
      {biglietti.map((b, i) => (
        <li
          // Due righe dello stesso prodotto nello stesso ordine hanno lo stesso
          // numero: la posizione rende la chiave unica (l'elenco non si riordina).
          key={`${b.ordine}-${b.numero}-${i}`}
          className={cn('flex flex-col gap-3 px-4 py-4 sm:px-6', b.stato === 'annullato' && 'opacity-60')}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <p className="text-lg font-semibold text-gray-900">
              Biglietto {b.numero} <span className="font-normal text-gray-600">· {b.prodotto}</span>
            </p>
            <p className="text-sm text-neutral-500">ordine n. {b.ordine}</p>
          </div>
          {(b.insieme ?? []).map((nome) => (
            <p key={nome} className="-mt-2 text-lg text-gray-700">
              + {nome}
            </p>
          ))}
          {quantiNelModulo(b) > 1 && b.stato === 'da_registrare' && (
            <p className="text-sm text-neutral-500">Un solo modulo: i dati valgono per tutti e due i biglietti.</p>
          )}
          <Stato b={b} />
          {b.stato === 'da_registrare' && b.token && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => onCompila(b)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-primary-600/30 transition hover:bg-primary-700"
              >
                <PenLine className="h-4 w-4" />
                Compila i dati
              </button>
              <CopiaLink token={b.token} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
